import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as HiIcons from 'react-icons/hi';
import * as RiIcons from 'react-icons/ri';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiArrowRight, FiPlay, FiCheck, FiSettings, FiZap, FiGlobe, FiPhone, FiMail } = FiIcons;
const { HiRocketLaunch } = HiIcons;
const { RiRobotFill, RiBrainFill, RiBuilding2Fill, RiCityFill } = RiIcons;

function App() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const features = [
    {
      icon: RiRobotFill,
      title: "Robotics & Industrial Automation",
      description: "We design and deploy automation systems for factories, warehouses, and logistics.",
      items: [
        "Smart manufacturing systems",
        "Robotic process automation", 
        "Warehouse robotics",
        "Conveyor and sorting automation",
        "Quality inspection AI",
        "Packaging & assembly automation"
      ],
      result: "Higher output. Lower cost. Zero downtime."
    },
    {
      icon: RiBrainFill,
      title: "AI Agents & Digital Workforce",
      description: "We create AI-powered agents that replace repetitive human work.",
      items: [
        "AI voice agents for calls & support",
        "AI chat agents for websites & apps",
        "AI sales & booking assistants",
        "AI onboarding & training agents",
        "AI customer service automation"
      ],
      result: "Faster service. Happier customers. Lower headcount."
    },
    {
      icon: FiSettings,
      title: "Business Process Automation",
      description: "We automate operations across your entire organization.",
      items: [
        "CRM & sales automation",
        "Marketing automation",
        "Order processing",
        "Inventory management",
        "HR & recruitment automation",
        "Finance & reporting automation"
      ],
      result: "Fewer errors. Faster execution. Massive efficiency gains."
    },
    {
      icon: RiCityFill,
      title: "Smart City & Infrastructure Systems",
      description: "We build intelligent systems for modern cities and estates.",
      items: [
        "Traffic & transport automation",
        "Smart CCTV & monitoring",
        "Disaster & flood detection",
        "Smart energy management",
        "Smart parking & access control",
        "Command & control centers"
      ],
      result: "Safer cities. Smarter governance. Better lives."
    }
  ];

  const benefits = [
    "Enterprise-grade engineering",
    "AI-first architecture",
    "Secure & scalable systems",
    "Custom-built solutions",
    "End-to-end implementation",
    "Long-term support & optimization"
  ];

  const clients = [
    "Manufacturing & industrial plants",
    "Logistics & warehousing companies",
    "BPOs & call centers",
    "Hospitals & healthcare groups",
    "Real estate & townships",
    "Retail & e-commerce brands",
    "Government & LGUs",
    "Smart estates & business parks"
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100">
      {/* Header */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-slate-100"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center group cursor-pointer">
            <span className="text-xl md:text-2xl font-extrabold tracking-tight text-slate-900">
              Boost<span className="text-blue-600">Automate</span>
            </span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-10">
            <a href="#solutions" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">Solutions</a>
            <a href="#benefits" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">Why Us</a>
            <a href="#clients" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">Industries</a>
            <button className="bg-slate-900 text-white px-6 py-2.5 rounded-full text-sm font-bold hover:bg-blue-600 hover:shadow-lg transition-all duration-300">
              Contact Us
            </button>
          </nav>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-40 pb-24 px-6 bg-gradient-to-b from-blue-50/50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="space-y-10"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center space-x-2 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full text-blue-700 text-xs font-bold uppercase tracking-wider">
              <SafeIcon icon={FiZap} className="text-sm" />
              <span>The Next Generation of Automation</span>
            </motion.div>

            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-8xl font-black text-slate-900 leading-[1.1] tracking-tight"
            >
              Revolutionizing <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Robotics & AI
              </span>
            </motion.h1>

            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto font-medium leading-relaxed"
            >
              Build faster. Operate smarter. Scale without limits. We design intelligent systems that help businesses, factories, and cities run on autopilot.
            </motion.p>

            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-5 justify-center items-center pt-4"
            >
              <button className="bg-blue-600 text-white px-10 py-5 rounded-2xl text-lg font-bold hover:bg-blue-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center space-x-3">
                <span>Request a Demo</span>
                <SafeIcon icon={FiPlay} className="text-lg" />
              </button>
              <button className="bg-white text-slate-900 border-2 border-slate-200 px-10 py-5 rounded-2xl text-lg font-bold hover:border-blue-600 hover:text-blue-600 transition-all duration-300 flex items-center space-x-3">
                <span>Talk to an Expert</span>
                <SafeIcon icon={FiArrowRight} className="text-lg" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 px-6 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight">
                The Future Runs <br /><span className="text-blue-600">on Automation</span>
              </h2>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-1.5 h-12 bg-red-500 rounded-full mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Manual processes can't keep up.</h3>
                    <p className="text-slate-600 mt-2">The world is moving too fast for traditional workflows to remain competitive.</p>
                  </div>
                </div>
                <p className="text-lg text-slate-600 leading-relaxed">
                  BoostAutomate helps organizations replace inefficiency with intelligent systems powered by AI and robotics. We build digital workforces that run 24/7—faster and cheaper than human-only teams.
                </p>
                <div className="inline-block px-6 py-3 bg-blue-50 text-blue-700 rounded-xl font-bold">
                  Your business, on autopilot.
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative rounded-3xl overflow-hidden shadow-2xl"
            >
              <img 
                src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&q=80&w=800" 
                alt="Automation Technology"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Build */}
      <section id="solutions" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-black text-slate-900">What We Build</h2>
            <div className="w-24 h-1.5 bg-blue-600 mx-auto mt-6 rounded-full" />
          </div>

          <div className="grid lg:grid-cols-2 gap-10">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-[2rem] p-10 border border-slate-200 shadow-sm hover:shadow-xl hover:border-blue-200 transition-all duration-300 group"
              >
                <div className="flex items-center space-x-5 mb-8">
                  <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center transition-colors group-hover:bg-blue-600 group-hover:text-white">
                    <SafeIcon icon={feature.icon} className="text-3xl" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900">{feature.title}</h3>
                </div>
                
                <p className="text-slate-600 mb-8 text-lg font-medium">{feature.description}</p>
                
                <ul className="grid sm:grid-cols-1 gap-4 mb-10">
                  {feature.items.map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-3 text-slate-700">
                      <div className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="text-xs font-bold" />
                      </div>
                      <span className="font-semibold text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="pt-6 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-blue-600 font-bold tracking-tight">PROJECTED OUTCOME</span>
                  <span className="bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-xs font-black uppercase">{feature.result}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="benefits" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
              Why Enterprise Leaders Choose Us
            </h2>
            <p className="text-xl text-slate-500 font-medium max-w-2xl mx-auto">
              We don't just sell software. We engineer the foundational infrastructure for the next era of industrial growth.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.02 }}
                className="flex items-center space-x-4 bg-slate-50 p-8 rounded-2xl border border-slate-100 hover:bg-white hover:border-blue-500 transition-all duration-300 shadow-sm"
              >
                <div className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiCheck} className="text-lg" />
                </div>
                <span className="text-slate-900 font-bold">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section id="clients" className="py-24 px-6 bg-slate-900 text-white rounded-[3rem] mx-6 my-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-black mb-6">Industries We Power</h2>
            <p className="text-slate-400 text-xl font-medium">
              Scaling operations through intelligent automation ecosystems.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {clients.map((client, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="bg-slate-800/50 p-8 rounded-3xl border border-slate-700 hover:border-blue-500 transition-all duration-300 text-center"
              >
                <SafeIcon icon={RiBuilding2Fill} className="text-blue-500 text-4xl mx-auto mb-4" />
                <p className="text-slate-200 font-bold leading-tight">{client}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-blue-600 to-indigo-700 rounded-[3rem] p-12 md:p-24 text-center text-white relative overflow-hidden shadow-2xl">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full blur-3xl -ml-32 -mb-32" />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative z-10"
          >
            <h2 className="text-4xl md:text-6xl font-black mb-8 leading-tight">
              Ready to automate <br />your future?
            </h2>
            <p className="text-xl text-blue-100 mb-12 font-medium max-w-2xl mx-auto">
              The next era of work is here. Don't just adapt to the change—lead it with BoostAutomate engineering.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center mt-12">
              <button className="bg-white text-blue-600 px-10 py-5 rounded-2xl text-lg font-black hover:bg-slate-100 hover:scale-105 transition-all duration-300 shadow-xl">
                Book a Strategy Call
              </button>
              <button className="bg-blue-500 text-white border-2 border-blue-400/30 px-10 py-5 rounded-2xl text-lg font-black hover:bg-blue-400 transition-all duration-300">
                Contact Sales
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1 lg:col-span-2 space-y-6">
              <div className="flex items-center">
                <span className="text-2xl font-black tracking-tight">BoostAutomate</span>
              </div>
              <p className="text-slate-500 max-w-md font-medium leading-relaxed">
                Revolutionizing Robotics, AI & Business Automation. We build the intelligent infrastructure that powers modern society.
              </p>
              <div className="flex space-x-4">
                {[FiMail, FiPhone, FiGlobe].map((Icon, i) => (
                  <a key={i} href="#" className="w-10 h-10 bg-white border border-slate-200 rounded-xl flex items-center justify-center text-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all">
                    <SafeIcon icon={Icon} />
                  </a>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6">Solutions</h4>
              <ul className="space-y-4 font-bold text-slate-600">
                <li><a href="#" className="hover:text-blue-600">Robotics</a></li>
                <li><a href="#" className="hover:text-blue-600">AI Agents</a></li>
                <li><a href="#" className="hover:text-blue-600">Digital Workforce</a></li>
                <li><a href="#" className="hover:text-blue-600">Smart Cities</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-black uppercase tracking-widest text-slate-400 mb-6">Company</h4>
              <ul className="space-y-4 font-bold text-slate-600">
                <li><a href="#" className="hover:text-blue-600">About Us</a></li>
                <li><a href="#" className="hover:text-blue-600">Expertise</a></li>
                <li><a href="#" className="hover:text-blue-600">Case Studies</a></li>
                <li><a href="#" className="hover:text-blue-600">Support</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-200 pt-10 text-center">
            <p className="text-slate-400 font-bold text-sm">
              © {new Date().getFullYear()} BoostAutomate. All rights reserved. Built for Growth. Designed for Scale.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;